import { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ToggleButton from 'react-toggle-button';

import {
  useSoyuzBrandConfig,
  getPagePath,
  trans,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';

import Metrics from '@spotahome/soyuz-tracking';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import Autocompleter from '../Autocompleter';
import Button from '../Button';
import { extractItem, setItem } from '../../../utils/localStorage';

import { FEATURED_CITIES } from '../utils/seoConstants';
import MagnifyingGlassIcon from '../icons/MagnifyingGlassIcon';

import { showFeedbackToast } from './CitySuggestionToast';

import styles from './CityAutocompleter.module.scss';

const CITY_KEY = 'sah-search-city';
const prevSearchedCity = extractItem(CITY_KEY);

const getInitialCityName = (cities, brandConfig) => {
  const brandCity = brandConfig.mainCity;
  if (!brandCity) {
    return prevSearchedCity || '';
  }

  return cities.find(city => city.id === brandCity)?.cityName || '';
};

const CityAutocompleter = ({
  cities,
  className = '',
  deviceType,
  iconColor = ''
}) => {
  const brandConfig = useSoyuzBrandConfig();
  const [city, setCity] = useState('');
  const [movingInWithSomeone, setMovingInWithSomeone] = useState(false);
  const autocompleterRef = useRef(null);

  const isMobileOrTablet = deviceType !== 'desktop';

  useEffect(() => {
    setCity(getInitialCityName(cities, brandConfig));
  }, [cities]);

  const shakeAutocompleterWrapper = () => {
    const EXECUTE_AFTER_MS = 1000;
    autocompleterRef.current.classList.toggle('city-selector--shaking');
    setTimeout(() => {
      autocompleterRef.current.classList.toggle('city-selector--shaking');
    }, EXECUTE_AFTER_MS);
  };

  const cityList = useMemo(
    () =>
      cities.reduce(
        (accumulator, currentCity) => ({
          ...accumulator,
          [currentCity.cityName]: {
            cityId: currentCity.id,
            countryId: currentCity.country?.countryId
          }
        }),
        {}
      ),
    [JSON.stringify(cities)]
  );

  const sendCitySelectedAction = cityId => {
    Metrics.actions.sendActionEvent(Metrics.actions.SELECT_CITY, {
      cityId,
      pageType: 'homepage'
    });
  };

  const navigateToSearchedCity = searchedCity => {
    const { cityId, countryId } = cityList[searchedCity];
    const locationString = `${cityId}--${countryId}`;
    const desiredCityUrl = getPagePath('marketplace.free-search', {
      params: { locationString }
    });

    SoyuzAnalytics.sendGA4Event('search', {
      search_term: cityId
    });
    window.location.href = desiredCityUrl;
  };

  const setCitySearchedHistory = searchedCity => {
    setItem(CITY_KEY, searchedCity);
  };

  const handleClickExploreButton = e => {
    e.preventDefault();

    if (!city) {
      shakeAutocompleterWrapper();
    }

    if (!cityList[city]) {
      return; // Note: users may type a city that we do not support, so we do not send them anywhere
    }

    setCitySearchedHistory(city);

    sendCitySelectedAction(cityList[city].cityId);
    navigateToSearchedCity(city);
  };

  const handleCityChange = desiredCity => setCity(desiredCity);

  const handleCitySelected = selectedCity => {
    setCity(selectedCity);

    sendCitySelectedAction(cityList[selectedCity].cityId);

    navigateToSearchedCity(selectedCity);
    setCitySearchedHistory(selectedCity);
  };

  const scrollIntoAutocompleter = () =>
    autocompleterRef.current.scrollIntoView({ behavior: 'smooth' });

  const handleAutocompleterClick = () => {
    if (isMobileOrTablet) {
      Metrics.hotjar.recordTag('HomepageCityAutocompleterMobile');

      setTimeout(scrollIntoAutocompleter, 500);
    } else {
      Metrics.hotjar.recordTag('HomepageCityAutocompleter');
    }
  };

  const handleSubmitSuggestion = () => {
    SoyuzAnalytics.sendGA4Event('city-suggestion', {
      action: city
    });
    showFeedbackToast();
  };

  const citySelectorClasses = classNames(styles['city-selector'], className);

  const isMovingWithSomeoneToguelinExperiment = useSoyuzExperiment(
    'ShowIsMovingWithSomeoneToguelin'
  );

  const showIsMovingWithSomeoneToguelin =
    !isMobileOrTablet &&
    isMovingWithSomeoneToguelinExperiment.get('behaviour') === 'variant';

  return (
    <>
      <div className={citySelectorClasses} ref={autocompleterRef}>
        <Autocompleter
          initialValue={city}
          minLength={1}
          list={Object.keys(cityList)}
          featuredResults={cities
            .filter(cityItem => FEATURED_CITIES.includes(cityItem.id))
            .map(cityItem => cityItem.cityName)}
          placeholder={trans('autocompleter.placeholder.city')}
          typingPlaceholder={trans('autocompleter.placeholder.typing-hint')}
          className={styles['city-selector__autocompleter']}
          dataTest="city-selector-autocomplete"
          onSelectedElement={handleCitySelected}
          onChange={handleCityChange}
          onClick={handleAutocompleterClick}
          onSubmitSuggestion={handleSubmitSuggestion}
          selectAllContentOnFocus={!isMobileOrTablet}
          otherCitySuggestionsEnabled
          newTheming={!isMobileOrTablet}
          iconColor={iconColor}
        />

        <Button
          type="button"
          fullWidth
          size="big"
          className={styles['city-selector__new-button']}
          data-test="city-selector-button"
          onClick={handleClickExploreButton}
        >
          <span className={styles['city-selector__new-button-text']}>
            {isMobileOrTablet
              ? trans('landingpage.city_selector.button.search-city')
              : trans('landingpage.city_selector.button.where-to-live')}
          </span>
          <span className={styles['city-selector__new-button-icon']}>
            <MagnifyingGlassIcon />
          </span>
        </Button>
      </div>
      {showIsMovingWithSomeoneToguelin && (
        <div className={styles['city-selector__moving-in-with-someone']}>
          <span
            className={styles['city-selector__moving-in-with-someone-text']}
          >
            {trans('landingpage.city_selector.moving-in-with-someone')}
          </span>
          <ToggleButton
            inactiveLabel={''}
            activeLabel={''}
            colors={{
              activeThumb: {
                base: 'rgb(10,7,119)'
              },
              inactiveThumb: {
                base: 'rgb(255,255,255)'
              },
              active: {
                base: 'rgb(207,221,245)'
              },
              inactive: {
                base: 'rgb(245,245,245)'
              }
            }}
            thumbAnimateRange={[0, 36]}
            thumbStyle={{
              position: 'absolute',
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            value={movingInWithSomeone}
            onToggle={value => {
              setMovingInWithSomeone(!value);
              SoyuzAnalytics.sendGA4Event('MovingInWithSomeone', {
                action: 'Selected',
                value: !value ? 'Yes' : 'No'
              });
            }}
          />
        </div>
      )}
    </>
  );
};

CityAutocompleter.propTypes = {
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      country: PropTypes.shape({
        countryId: PropTypes.string
      })
    })
  ).isRequired,
  className: PropTypes.string,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired,
  iconColor: PropTypes.string
};

export default CityAutocompleter;
